import { type JSX } from 'react';
import { formatDistanceToNowStrict } from 'date-fns';
import { HashInputField } from '@/shared/components/atoms/HashInputField';
import { ProgressBar } from '@/shared/components/atoms/ProgressBar';
import { Typography } from '@/shared/components/atoms/Typography';
import useAuction from '@/shared/hooks/useAuction';
import useToast from '@/shared/hooks/useToast';
import FlipAmount from '@/shared/utils/FlipAmount';
import type TokenAmount from '@/shared/utils/TokenAmount';
import { FundAccountModal, type FundAccountModalProps } from './FundAccountModal';

export interface AddFundsModalProps extends Pick<FundAccountModalProps, 'active' | 'onClose'> {
  nodeValues: {
    nodeId: string;
    nodePublicKey: string;
    nodeCurrentBalance: TokenAmount;
  } | null;
}

export const AddFundsModal = ({ nodeValues, onClose, active }: AddFundsModalProps): JSX.Element => {
  const toast = useToast('redemption');
  const { auction } = useAuction();
  const auctionEndsIn = formatDistanceToNowStrict(auction?.endsAt || 0);

  return (
    <FundAccountModal
      type="increase"
      active={active}
      onClose={onClose}
      nodeAddress={nodeValues?.nodePublicKey ?? ''}
      nodeCurrentBalance={nodeValues?.nodeCurrentBalance ?? FlipAmount.ZERO}
    >
      <div className="flex items-center">
        <div className="flex flex-1 flex-col">
          <Typography color="white" variant="label-large">
            Current Auction
          </Typography>
          <Typography variant="label" elementType="span" className="flex">
            {auction?.hasEnded ? 'Ended' : 'Ending in'} {auctionEndsIn}
            {auction?.hasEnded && ` ago`}
          </Typography>
        </div>
        <div>
          <Typography color="white" variant="heading1">
            {auction?.percentageElapsed.toFixed(2)}%
          </Typography>
        </div>
      </div>
      <div className="w-full pb-4">
        <ProgressBar value={auction?.percentageElapsed ?? 0} variant="active" />
      </div>
      <HashInputField
        id="nodeIdInput"
        onCopy={() => {
          toast.success(`Copied to clipboard`);
        }}
        value={nodeValues?.nodeId ?? ''}
        copyButton
      />
    </FundAccountModal>
  );
};
